/*
* Add Association view for authenticated users
*/

import { filterBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import { countriesList, statesList } from '../config/geolocations';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import Auth from '../auth/auth';
import { AuthConfig } from '../config/authconfiguration';

export const DefaultAssociationDetailsValues = {
    None: 'None',
    NotHoa: 'Not HOA',
    No: 'No',
    CompanyId: '1',
    FiscalYearEnd: '12/31',
};

export interface IAssociationAddProps extends AppComp.IAppComponentProps {
    lookups?: any;
    newAssociation?: any;
    newOrder?: any;
    lastRequestDate?: any;
    navigateTo?: any;
}

export interface IAssociationAddState extends AppComp.IAppComponentState {
    formChanges?: any;
    skipClient?: any;
    filterClient?: any;
}

class AddAssociation extends AppComp.AppComponent<IAssociationAddProps, IAssociationAddState> {
    public constructor(props: IAssociationAddProps) {
        super(props, 'app.association-add.');

        this.state = {
            formChanges: {},
            skipClient: 0,
            filterClient: undefined,
        };
    }

    private originalFormDataCache: any = {};
    private kendoFormWhiteBoxSectionProps: any = {};
    private lastRequestDateCache: number = 0;
    private readonly virtualizedDropdownPageSize: number = 20;
    private saveInProgress: boolean = false;

    public componentDidUpdate(): void {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // HANDLERS
    public handleSaveAssociationButton = (e: any, createOrder: boolean = false): void => {
        if (this.saveInProgress) {
            return;
        }

        this.saveInProgress = true;
        this.performSave(e, createOrder);
    }

    public performSave = async (e: any, createOrder: boolean = false): Promise<void> => {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { newAssociation: this.getDefaultnewAssociationObject() },
            { newAssociation: this.state.formChanges.newAssociation },
        );

        // console.log(`handleSaveAssociationButton createOrder ${createOrder} changeData`, changeData);

        this.props.newAssociation(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                // FORCE RELOAD
                const newAssociationId = _.get(response, 'body.associationId', 0);
                const newAssociationClientId = _.get(response, 'body.client.clientId', 0);

                // console.log(`handleSaveAssociationButton response newAssociationId ${newAssociationId} newAssociationClientId ${newAssociationClientId}`);

                if (!createOrder) {
                    // Redirect to assoc detail
                    this.saveInProgress = false;

                    let auth = new Auth();
                    const lsRedirectToAddBid = Formatters.bool(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid));

                    if (lsRedirectToAddBid) {
                        auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewAssociationId, newAssociationId);
                        this.props.navigateTo(NavigationLinks.orderAddBid());
                    } else {
                        this.props.navigateTo(NavigationLinks.associationDetail(newAssociationId));
                    }
                } else {
                    // Create order for new assoc
                    const createOrderData = _.merge(
                        {},
                        { association: { associationId: newAssociationId } },
                        { client: { clientId: newAssociationClientId } },
                    );

                    this.props.newOrder(createOrderData, token, false).then(orderResponse => {
                        // We can promise-chain on the mutation action to trigger state
                        // transitions when it succeeds or fails.

                        if (orderResponse.status >= 200 && orderResponse.status < 300) {
                            this.originalFormDataCache = {};

                            // FORCE RELOAD
                            const newOrderId = _.get(orderResponse, 'body.orderId');

                            this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
                        } else {
                            // Error during creation...
                        }
                    }).finally(() => {
                        this.saveInProgress = false;
                    });
                }
            } else {
                // Error during creation...
                this.saveInProgress = false;
            }
        }).catch(error => {
            this.saveInProgress = false;
        });
    }

    public handleClientDropdownPageChange(event: any, formData: any): void {
        this.setState({
            skipClient: event.page.skip
        });
    }

    public handleClientDropdownFilterChange(event: any, formData: any): void {
        this.setState({
            skipClient: 0,
            filterClient: event.filter,
        });
    }

    public handleFormClientIdInputChange(e: any, x: any): void {
        const fieldName = _.get(e, 'target.name', '');
        const newValue = _.get(e, 'target.value', '');

        // console.log(`handleFormClientIdInputChange fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        const { lookups } = this.props;
        const lookupsClients = _.get(lookups, LK.LookupKeys.Clients, []);

        const associationClient = _.find(lookupsClients, ['clientId', Number(newValue)]);
        const associationClientContacts = _.get(associationClient, 'contacts', []);
        const associationClientContact = _.first(associationClientContacts);

        let associationContactId = (Formatters.getInt(associationClientContact, 'contactId')) ? Formatters.getStr(associationClientContact, 'contactId') : '';

        let prevChanges = this.state.formChanges;
        _.set(prevChanges, 'newAssociation.client.clientId', newValue);
        _.set(prevChanges, 'newAssociation.contact.contactId', associationContactId);

        this.setState({
            formChanges: prevChanges
        });
    }

    public handleFormInputChange(e: any, x: any): void {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });

        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    // console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public buildLookupOptionsByFields(records: any, valueFieldName: string, textFieldName: string) {
        return (records && _.size(records)) ?
            records.map((c) => _.merge({}, {
                value: Formatters.getStr(c, valueFieldName),
                text: Formatters.getStr(c, textFieldName)
            })) : [];
    }

    public buildLookupOptionsWith(records: any, valueFieldName: string, textIterateeFunc: (obj: any) => any) {
        return (records && _.size(records)) ?
            records.map((c) => _.merge({}, {
                value: Formatters.getStr(c, valueFieldName),
                text: String(textIterateeFunc(c)),
            })) : [];
    }

    public getOptions(records: any) {
        if (records && _.size(records)) {
            return records;
        } else {
            return [];
        }
    }

    public getFormData(
        clients: any, associationTypes: any, companies: any, states: any, countries: any,
        streets: any, roofs: any, paintedExteriors: any, fencing: any, interiorHallways: any, irrigation: any, secured: any,
        mechanical: any, elevators: any, clubhouses: any) {
        const defNewAssoc = this.getDefaultnewAssociationObject();
        const fcNewAssoc = _.get(this.state, 'formChanges.newAssociation');
        const newAssocMerged = _.merge(defNewAssoc, fcNewAssoc);

        // const association = _.merge({}, this.state.formChanges);
        // let newAssociation = _.get(association, 'newAssociation', this.getDefaultnewAssociationObject());
        // let newAssociation = _.get(assocMerged, 'newAssociation', this.getDefaultnewAssociationObject());

        let newAssocClientOptions: any = _.stubArray();
        if (_.size(clients)) {
            const assocClients = clients;

            newAssocClientOptions = _.orderBy(assocClients, [a => a.name.toLowerCase()], ['asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'clientId'), text: Formatters.getStr(c, 'name') }));

            if (this.state.filterClient) {
                newAssocClientOptions = filterBy(newAssocClientOptions, this.state.filterClient);
            }
        }

        const associationStateOptions = this.buildLookupOptionsByFields(states, 'stateCode', 'name');
        const associationCountryOptions = this.buildLookupOptionsByFields(countries, 'countryCode', 'name');

        // const associationClientIdOptions = this.buildLookupOptionsByFields(clients, 'clientId', 'name');
        const associationClient = _.find(clients, ['clientId', Formatters.getInt(newAssocMerged, 'client.clientId')]);
        const associationClientContacts = _.get(associationClient, 'contacts', []);
        const associationContactOptions = this.buildLookupOptionsWith(associationClientContacts, 'contactId', (o) => Formatters.composeClientContactFormal(o));

        const associationTypeOptions = this.buildLookupOptionsByFields(associationTypes, 'associationTypeId', 'name');
        const associationCompanyOptions = this.buildLookupOptionsByFields(companies, 'companyId', 'name');

        const associationStreetOptions = this.buildLookupOptionsByFields(streets, 'name', 'name');
        const associationRoofOptions = this.buildLookupOptionsByFields(roofs, 'name', 'name');
        const associationPaintedExteriorOptions = this.buildLookupOptionsByFields(paintedExteriors, 'name', 'name');
        const associationFencingOptions = this.buildLookupOptionsByFields(fencing, 'name', 'name');
        const associationInteriorHallwayOptions = this.buildLookupOptionsByFields(interiorHallways, 'name', 'name');
        const associationIrrigationOptions = this.buildLookupOptionsByFields(irrigation, 'name', 'name');
        const associationSecuredOptions = this.buildLookupOptionsByFields(secured, 'name', 'name');
        const associationMechanicalOptions = this.buildLookupOptionsByFields(mechanical, 'name', 'name');
        const associationElevatorOptions = this.buildLookupOptionsByFields(elevators, 'name', 'name');
        const associationClubhouseOptions = this.buildLookupOptionsByFields(clubhouses, 'name', 'name');

        let result = {
            newAssociation: newAssocMerged,

            newAssocClientOptions: newAssocClientOptions,
            // associationClientIdOptions: this.getOptions(associationClientIdOptions),
            associationStateOptions: this.getOptions(associationStateOptions),
            associationCountryOptions: this.getOptions(associationCountryOptions),

            associationTypeOptions: this.getOptions(associationTypeOptions),
            associationContactOptions: this.getOptions(associationContactOptions),
            associationCompanyOptions: this.getOptions(associationCompanyOptions),

            associationStreetOptions: this.getOptions(associationStreetOptions),
            associationRoofOptions: this.getOptions(associationRoofOptions),
            associationPaintedExteriorOptions: this.getOptions(associationPaintedExteriorOptions),
            associationFencingOptions: this.getOptions(associationFencingOptions),
            associationInteriorHallwayOptions: this.getOptions(associationInteriorHallwayOptions),
            associationIrrigationOptions: this.getOptions(associationIrrigationOptions),
            associationSecuredOptions: this.getOptions(associationSecuredOptions),
            associationMechanicalOptions: this.getOptions(associationMechanicalOptions),
            associationElevatorOptions: this.getOptions(associationElevatorOptions),
            associationClubhouseOptions: this.getOptions(associationClubhouseOptions),
        };

        const res = _.merge({}, result); // , this.state.formChanges);
        // console.log(`getformdata return res`, { res, newAssocMerged, fcNewAssoc, formChanges: this.state.formChanges  });
        return res;
    }

    public getDefaultnewAssociationObject() {
        return _.cloneDeep({
            associationType: {
                associationTypeId: '',
            },
            client: {
                clientId: '',
            },
            company: {
                companyId: '',
            },
            contact: {
                contactId: '',
            },
            details: {
                constructDate: '',
                converDate: '',
                elevators: DefaultAssociationDetailsValues.No,
                executiveSummaryDetails: '',
                fencing: DefaultAssociationDetailsValues.No,
                fiscalYearEnd: DefaultAssociationDetailsValues.FiscalYearEnd,
                inactive: false,
                interiorHalls: DefaultAssociationDetailsValues.No,
                irrigation: DefaultAssociationDetailsValues.No,
                mechanical: DefaultAssociationDetailsValues.No,
                numberBuildings: 0,
                numberStories: 0,
                numberUnits: 0,
                phases: '',
                pools: 0,
                recRooms: DefaultAssociationDetailsValues.No,
                roofs: DefaultAssociationDetailsValues.NotHoa,
                secured: DefaultAssociationDetailsValues.No,
                siding: DefaultAssociationDetailsValues.NotHoa,
                spas: 0,
                streets: DefaultAssociationDetailsValues.None,
                tennis: 0,

                // new hoa detail RF22
                trashEnclosures: false,
                monumentSigns: false,
                bikeWalkingPaths: false,
                streetLighting: false,
                streetSigns: false,
                outdoorFurniture: false,
                awnings: false,
                mailboxes: false,
                walkways: false,
                fireEscapes: false,
                buildingLighting: false,
                decksBalconies: false,
                carports: false,
                garages: false,
                exteriorStairways: false,
                waterscapesFountains: false,
                playgroundEquipment: false,
                gazebos: false,

                // new hoa amenities number fields per rf22
                wadingPools: 0,
                saunas: 0,
                marinasDocks: 0,
                generators: 0,
                waterHeaters: 0,
                boilers: 0,
                hvacUnits: 0,
                chillers: 0,
                maintenanceBuildings: 0,
                volleyballCourts: 0,
                basketballCourts: 0,

                // new hoa amenities building interiors boolean fields per rf22
                interiorLighting: false,
                interiorPainting: false,
                interiorFloors: false,
                office: false,
                laundryEquipment: false,
                interiorElevators: false,
                kitchen: false,
                gym: false,
                restrooms: false,
            },
            location: {
                address1: '',
                address2: '',
                city: '',
                countryCode: 'US',
                postalCode: '',
                stateCode: 'NV',
            },
            name: '',
            notes: '',
        });
    }

    public getFormChangesCount(): number {
        return _.size(_.get(this.state, 'formChanges.newAssociation'));
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        // Props values
        const prevVal = _.get(this.props, 'lastRequestDate');

        // Cached values
        const cacheVal = this.lastRequestDateCache;

        // Determine if reset required
        resetFormData = (_.isEqual(prevVal, cacheVal)) ? false : true;

        // console.log(`getFormChangesResetRequired prevVal ${prevVal} cacheVal ${cacheVal} resetFormData ${resetFormData}`);

        return resetFormData;
    }

    // RENDERERS
    public renderHeaderButtonToolbar = (): JSX.Element => {
        let addAssociationButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        let addAssociationWithOrderButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            addAssociationButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: this.appText('add-association'),
            };

            addAssociationWithOrderButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: this.appText('add-association-with-order'),
            };
        }

        // Disable/Hide "add association with order" button if user is in "add new bid" workflow (they are adding a new association for the new bid)
        let auth = new Auth();
        const lsRedirectToAddBid = Formatters.bool(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid));
        if (lsRedirectToAddBid) { addAssociationWithOrderButtonProps.disabled = true; }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={6}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button {...addAssociationButtonProps} onClick={e => { this.handleSaveAssociationButton(e, false); }}><i className="glyphicon glyphicon-floppy-disk" /> {addAssociationButtonProps.title}</Button>
                                </ButtonGroup>

                                <ButtonGroup>
                                    {(addAssociationWithOrderButtonProps.disabled) ?
                                        (<div />) : (
                                            <Button
                                                {...addAssociationWithOrderButtonProps}
                                                onClick={e => { this.handleSaveAssociationButton(e, true); }}
                                            >
                                                <i className="glyphicon glyphicon-floppy-disk" /> {addAssociationWithOrderButtonProps.title}
                                            </Button>)}
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionNewAssociation(formData: any): JSX.Element {
        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('add-association')}>
                <Row>
                    <Col xs={4} xl={4}>
                        {this.renderNewAssociationFormLocation(formData)}
                        {this.renderNewAssociationFormConstructionInfo(formData)}
                        {this.renderNewAssociationFormCommonAmenities(formData)}
                    </Col>

                    <Col xs={8} xl={8}>
                        {this.renderNewAssociationFormAssociationDetails(formData)}
                        {this.renderNewAssociationFormAmenityResponsibilities(formData)}
                        {this.renderNewAssociationFormNotes(formData)}
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderNewAssociationFormLocation(formData: any) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsDropdownDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 3,
        });

        let inputPropsMedWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 4,
        });

        let inputPropsLargeWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.location')}>
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.name"
                        label={this.appText('association-name')}
                        placeholder={this.appText('association-name.placeholder')}
                        value={Formatters.getStr(formData, 'newAssociation.name')}
                        required={true}
                        {...inputPropsLargeWidthTextDebounced}
                    />

                    <DetailFormCommon.InventoryDetailFormSectionSpacing />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.location.address1"
                        label={this.appText('address')}
                        placeholder={this.appText('address.placeholder')}
                        required={true}
                        value={Formatters.getStr(formData, 'newAssociation.location.address1')}
                        {...inputPropsLargeWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.location.address2"
                        label={this.appText('address2')}
                        placeholder={this.appText('address2.placeholder')}
                        value={Formatters.getStr(formData, 'newAssociation.location.address2')}
                        {...inputPropsLargeWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.location.city"
                        label={this.appText('city')}
                        placeholder={this.appText('city.placeholder')}
                        required={true}
                        value={Formatters.getStr(formData, 'newAssociation.location.city')}
                        {...inputPropsMedWidthTextDebounced}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.location.stateCode"
                        label={this.appText('state')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'associationStateOptions', [])}
                        value={_.get(formData, 'newAssociation.location.stateCode', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.location.stateCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />
                    { /* <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.location.countryCode"
                        label={this.appText('country')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'associationCountryOptions', [])}
                        value={_.get(formData, 'newAssociation.location.countryCode', '')}
                        onChange={(o) => { this.handleFormInputChange({target: { name: 'newAssociation.location.countryCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                        {...inputPropsDropdownDebounced}
                    /> */ }
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.location.postalCode"
                        label={this.appText('zip')}
                        required={false}
                        value={Formatters.getStr(formData, 'newAssociation.location.postalCode')}
                        {...inputPropsSmallWidthTextDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderNewAssociationFormConstructionInfo(formData: any) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsHFIC75 = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
            colLabelXs: 5,
        });

        const inputPropsSmallWidthNumberInput = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 5,
            colInputXs: 7,
        });

        const inputPropsTextAreaDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.construction-info')}>
                    <KendoWrappers.KendoNumberInput
                        name="newAssociation.details.numberUnits"
                        label={this.appText('number-of-units')}
                        value={Formatters.getInt(formData, 'newAssociation.details.numberUnits')}
                        format="#####"
                        {...inputPropsSmallWidthNumberInput}
                    />
                    <KendoWrappers.KendoNumberInput
                        name="newAssociation.details.numberBuildings"
                        label={this.appText('number-of-buildings')}
                        format="#####"
                        value={Formatters.getInt(formData, 'newAssociation.details.numberBuildings')}
                        {...inputPropsSmallWidthNumberInput}
                    />
                    <KendoWrappers.KendoNumberInput
                        name="newAssociation.details.numberStories"
                        label={this.appText('number-of-stories')}
                        format="#####"
                        value={Formatters.getInt(formData, 'newAssociation.details.numberStories')}
                        {...inputPropsSmallWidthNumberInput}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.details.phases"
                        label={this.appText('phases')}
                        value={Formatters.getStr(formData, 'newAssociation.details.phases')}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.details.fiscalYearEnd"
                        label={this.appText('fiscal-year-end')}
                        value={Formatters.getStr(formData, 'newAssociation.details.fiscalYearEnd')}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.details.constructDate"
                        label={this.appText('construction-date')}
                        value={Formatters.getStr(formData, 'newAssociation.details.constructDate')}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newAssociation.details.converDate"
                        label={this.appText('conversion-date')}
                        value={Formatters.getStr(formData, 'newAssociation.details.converDate')}
                        {...inputPropsHFIC75}
                    />

                    <DetailFormCommon.InventoryDetailFormSectionSpacing />

                    <KendoWrappers.KendoTextareaDebounced
                        name="newAssociation.details.executiveSummaryDetails"
                        rows={4}
                        label={this.appText('executive-summary-details')}
                        value={Formatters.getStr(formData, 'newAssociation.details.executiveSummaryDetails')}
                        {...inputPropsTextAreaDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderNewAssociationFormCommonAmenities(formData: any) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsHFIC75 = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
            colLabelXs: 5,
        });

        const inputPropsTextAreaDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.common-area-amenities')}>
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.streets"
                        label={this.appText('streets')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationStreetOptions', [])}
                        value={_.get(formData, 'newAssociation.details.streets', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.streets', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.roofs"
                        label={this.appText('roofs')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationRoofOptions', [])}
                        value={_.get(formData, 'newAssociation.details.roofs', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.roofs', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.siding"
                        label={this.appText('painted-exteriors')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationPaintedExteriorOptions', [])}
                        value={_.get(formData, 'newAssociation.details.siding', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.siding', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.fencing"
                        label={this.appText('fencing')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationFencingOptions', [])}
                        value={_.get(formData, 'newAssociation.details.fencing', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.fencing', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.interiorHalls"
                        label={this.appText('interior-hallways')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationInteriorHallwayOptions', [])}
                        value={_.get(formData, 'newAssociation.details.interiorHalls', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.interiorHalls', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.irrigation"
                        label={this.appText('irrigation')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationIrrigationOptions', [])}
                        value={_.get(formData, 'newAssociation.details.irrigation', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.irrigation', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.secured"
                        label={this.appText('secured-property')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationSecuredOptions', [])}
                        value={_.get(formData, 'newAssociation.details.secured', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.secured', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.details.recRooms"
                        label={this.appText('clubhouse-rec-room')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'associationClubhouseOptions', [])}
                        value={_.get(formData, 'newAssociation.details.recRooms', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.details.recRooms', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsHFIC75}
                    />

                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderNewAssociationFormAssociationDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 3, colInputXs: 5 };

        let inputPropsDropdownDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsRoleCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        const associationContactDisabled = (Formatters.getInt(formData, 'newAssociation.client.clientId') <= 0);

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.details')}>
                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.associationType.associationTypeId"
                        label={this.appText('association-type')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'associationTypeOptions', [])}
                        value={_.get(formData, 'newAssociation.associationType.associationTypeId', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.associationType.associationTypeId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />

                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.company.companyId"
                        label={this.appText('company')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'associationCompanyOptions', [])}
                        value={_.get(formData, 'newAssociation.company.companyId', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.company.companyId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />

                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.client.clientId"
                        clearable={true}
                        filterable={true}
                        onFilterChange={(e) => { this.handleClientDropdownFilterChange(e, formData); }}
                        label={this.appText('client')}
                        value={_.get(formData, 'newAssociation.client.clientId', '')}
                        required={true}
                        data={_.get(formData, 'newAssocClientOptions', []).slice(this.state.skipClient, this.state.skipClient + this.virtualizedDropdownPageSize)}
                        virtual={{
                            total: _.size(formData.newAssocClientOptions),
                            pageSize: this.virtualizedDropdownPageSize,
                            skip: this.state.skipClient,
                        }}
                        onPageChange={(e) => { this.handleClientDropdownPageChange(e, formData); }}
                        textField="text"
                        valueField="value"
                        onChange={(o) => {
                            this.handleFormClientIdInputChange({ target: { name: 'newAssociation.client.clientId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                        }}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoDropdownListInput
                        name="newAssociation.contact.contactId"
                        label={this.appText('contact')}
                        textField="text"
                        valueField="value"
                        required={true}
                        disabled={associationContactDisabled}
                        data={_.get(formData, 'associationContactOptions', [])}
                        value={_.get(formData, 'newAssociation.contact.contactId', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newAssociation.contact.contactId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />

                    <DetailFormCommon.InventoryDetailFormSectionSpacing />

                    <KendoWrappers.KendoCheckbox
                        name="newAssociation.details.inactive"
                        label={this.appText('inactive')}
                        value={_.get(formData, 'newAssociation.details.inactive')}
                        {...inputPropsRoleCheckbox}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderNewAssociationFormAmenityResponsibilities(formData: any): JSX.Element {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        const inputPropsCheckbox = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 7,
            colInputXs: 5,
        });

        let inputPropsNumeric = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 6,
            colInputXs: 6,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.amenity-responsibilities-exterior')}>
                    <Row>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.trashEnclosures"
                                    label={this.appText('trash-enclosures')}
                                    value={formData.newAssociation.details.trashEnclosures}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.monumentSigns"
                                    label={this.appText('monument-signs')}
                                    value={formData.newAssociation.details.monumentSigns}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.bikeWalkingPaths"
                                    label={this.appText('bike-walking-paths')}
                                    value={formData.newAssociation.details.bikeWalkingPaths}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.streetLighting"
                                    label={this.appText('street-lighting')}
                                    value={formData.newAssociation.details.streetLighting}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.streetSigns"
                                    label={this.appText('street-signs')}
                                    value={formData.newAssociation.details.streetSigns}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.outdoorFurniture"
                                    label={this.appText('outdoor-furniture')}
                                    value={formData.newAssociation.details.outdoorFurniture}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.awnings"
                                    label={this.appText('awnings')}
                                    value={formData.newAssociation.details.awnings}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.mailboxes"
                                    label={this.appText('mailboxes')}
                                    value={formData.newAssociation.details.mailboxes}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.walkways"
                                    label={this.appText('walkways')}
                                    value={formData.newAssociation.details.walkways}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.fireEscapes"
                                    label={this.appText('fire-escapes')}
                                    value={formData.newAssociation.details.fireEscapes}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.buildingLighting"
                                    label={this.appText('building-lighting')}
                                    value={formData.newAssociation.details.buildingLighting}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.decksBalconies"
                                    label={this.appText('decks-balconies')}
                                    value={formData.newAssociation.details.decksBalconies}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.carports"
                                    label={this.appText('carports')}
                                    value={formData.newAssociation.details.carports}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.garages"
                                    label={this.appText('garages')}
                                    value={formData.newAssociation.details.garages}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.exteriorStairways"
                                    label={this.appText('exterior-stairways')}
                                    value={formData.newAssociation.details.exteriorStairways}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.waterscapesFountains"
                                    label={this.appText('waterscapes-fountains')}
                                    value={formData.newAssociation.details.waterscapesFountains}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.playgroundEquipment"
                                    label={this.appText('playground-equipment')}
                                    value={formData.newAssociation.details.playgroundEquipment}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.gazebos"
                                    label={this.appText('gazebos')}
                                    value={formData.newAssociation.details.gazebos}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                    </Row>
                </DetailFormCommon.InventoryDetailFormSection>

                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.amenity-responsibilities-exterior')}>
                    <Row>
                        <Col xs={6}>
                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.pools"
                                    label={this.appText('pools')}
                                    value={formData.newAssociation.details.pools}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.spas"
                                    label={this.appText('spas')}
                                    value={formData.newAssociation.details.spas}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.wadingPools"
                                    label={this.appText('wading-pools')}
                                    value={formData.newAssociation.details.wadingPools}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.saunas"
                                    label={this.appText('saunas')}
                                    value={formData.newAssociation.details.saunas}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.marinasDocks"
                                    label={this.appText('marinas-docks')}
                                    value={formData.newAssociation.details.marinasDocks}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.generators"
                                    label={this.appText('generators')}
                                    value={formData.newAssociation.details.generators}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.waterHeaters"
                                    label={this.appText('water-heaters')}
                                    value={formData.newAssociation.details.waterHeaters}
                                    {...inputPropsNumeric}
                                />
                            </div>
                        </Col>
                        <Col xs={6}>
                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.boilers"
                                    label={this.appText('boilers')}
                                    value={formData.newAssociation.details.boilers}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.hvacUnits"
                                    label={this.appText('hvac-units')}
                                    value={formData.newAssociation.details.hvacUnits}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.chillers"
                                    label={this.appText('chillers')}
                                    value={formData.newAssociation.details.chillers}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.maintenanceBuildings"
                                    label={this.appText('maintenance-buildings')}
                                    value={formData.newAssociation.details.maintenanceBuildings}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.volleyballCourts"
                                    label={this.appText('volleyball-courts')}
                                    value={formData.newAssociation.details.volleyballCourts}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.basketballCourts"
                                    label={this.appText('basketball-courts')}
                                    value={formData.newAssociation.details.basketballCourts}
                                    {...inputPropsNumeric}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoNumberInput
                                    name="newAssociation.details.tennis"
                                    label={this.appText('tennis')}
                                    value={formData.newAssociation.details.tennis}
                                    {...inputPropsNumeric}
                                />
                            </div>
                        </Col>
                    </Row>
                </DetailFormCommon.InventoryDetailFormSection>

                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.amenity-responsibilities-interior')}>
                    <Row>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.interiorLighting"
                                    label={this.appText('interior-lighting')}
                                    value={formData.newAssociation.details.interiorLighting}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.interiorPainting"
                                    label={this.appText('interior-painting')}
                                    value={formData.newAssociation.details.interiorPainting}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.interiorFloors"
                                    label={this.appText('interior-flooring')}
                                    value={formData.newAssociation.details.interiorFloors}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.office"
                                    label={this.appText('office')}
                                    value={formData.newAssociation.details.office}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.laundryEquipment"
                                    label={this.appText('laundry-equipment')}
                                    value={formData.newAssociation.details.laundryEquipment}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.interiorElevators"
                                    label={this.appText('interior-elevators')}
                                    value={formData.newAssociation.details.interiorElevators}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                        <Col xs={4}>
                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.kitchen"
                                    label={this.appText('kitchen')}
                                    value={formData.newAssociation.details.kitchen}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.gym"
                                    label={this.appText('gym')}
                                    value={formData.newAssociation.details.gym}
                                    {...inputPropsCheckbox}
                                />
                            </div>

                            <div>
                                <KendoWrappers.KendoCheckbox
                                    name="newAssociation.details.restrooms"
                                    label={this.appText('restrooms')}
                                    value={formData.newAssociation.details.restrooms}
                                    {...inputPropsCheckbox}
                                />
                            </div>
                        </Col>
                    </Row>
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderNewAssociationFormNotes(formData: any): JSX.Element {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        const inputPropsTextAreaDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('section-title.notes')}>
                    <KendoWrappers.KendoTextareaDebounced
                        name="newAssociation.notes"
                        rows={4}
                        label={this.appText('notes')}
                        value={Formatters.getStr(formData, 'newAssociation.notes')}
                        {...inputPropsTextAreaDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public render(): JSX.Element {
        const { lookups, lastRequestDate, ...rest } = this.props;

        const lookupsClients = _.get(lookups, LK.LookupKeys.Clients, []);
        const lookupsAssociationTypes = _.get(lookups, LK.LookupKeys.AssociationTypes, []);
        const lookupsCompanies = _.get(lookups, LK.LookupKeys.Companies, []);
        const lookupsStates = _.cloneDeep(statesList);
        const lookupsCountries = _.cloneDeep(countriesList);

        const lookupsStreets = _.get(lookups, LK.LookupKeys.StreetTypes, []);
        const lookupsRoofs = _.get(lookups, LK.LookupKeys.RoofTypes, []);
        const lookupsPaintedExteriors = _.get(lookups, LK.LookupKeys.SidingTypes, []);
        const lookupsFencing = _.get(lookups, LK.LookupKeys.FencingTypes, []);
        const lookupsInteriorHallways = _.get(lookups, LK.LookupKeys.InteriorHallTypes, []);
        const lookupsIrrigation = _.get(lookups, LK.LookupKeys.IrrigationTypes, []);
        const lookupsSecured = _.get(lookups, LK.LookupKeys.SecuredTypes, []);
        const lookupsMechanical = _.get(lookups, LK.LookupKeys.MechanicalTypes, []);
        const lookupsElevators = _.get(lookups, LK.LookupKeys.ElevatorTypes, []);
        const lookupsClubhouses = _.get(lookups, LK.LookupKeys.RecRoomTypes, []);

        let pageTitle = this.appText('page-title');
        let page = (<div />);

        if (_.size(lookupsClients) && _.size(lookupsAssociationTypes)) {
            const formData = this.getFormData(
                lookupsClients, lookupsAssociationTypes, lookupsCompanies, lookupsStates, lookupsCountries,
                lookupsStreets, lookupsRoofs, lookupsPaintedExteriors, lookupsFencing, lookupsInteriorHallways, lookupsIrrigation,
                lookupsSecured, lookupsMechanical, lookupsElevators, lookupsClubhouses);

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.lastRequestDateCache = lastRequestDate;
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    <Row>
                        <Col xs={12} xl={12}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar()}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    {this.renderSectionNewAssociation(formData)}
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar()}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.AssociationTypes,
    LK.LookupKeys.Clients,
    LK.LookupKeys.Companies,
    LK.LookupKeys.ElevatorTypes,
    LK.LookupKeys.FencingTypes,
    LK.LookupKeys.InteriorHallTypes,
    LK.LookupKeys.IrrigationTypes,
    LK.LookupKeys.MechanicalTypes,
    LK.LookupKeys.RecRoomTypes,
    LK.LookupKeys.RoofTypes,
    LK.LookupKeys.SecuredTypes,
    LK.LookupKeys.SidingTypes,
    LK.LookupKeys.StreetTypes,
]);

const mapPropsToConfigs = (props) => {
    return ReduxQueries.Queries.getLookupsByKeyRequest(
        getApiUrlLookups(),
        ReduxQueries.PropsAccessToken(props)
    );
};

const mapStateToProps = (state, props) => {
    const mapped = ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {
        lastRequestDate: new Date().getTime(),
    });

    return mapped;
};

const mapDispatchToProps = {
    newAssociation: MutateActions.newAssociationMutateAsync,
    newOrder: MutateActions.newOrderMutateAsync,
    navigateTo: (path) => push(path),
};

const AddClientContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AddAssociation);

export default withRouter(AddClientContainer);



// WEBPACK FOOTER //
// ./src/association/add.tsx